import {HStack} from "@chakra-ui/react";
import {Owner, TeamMin} from "platformed-client/types";
import {useCallback, useState} from "react";
import {TeamPicker, TeamPickerValue} from "../../components/TeamPicker.tsx";
import api from "../../api/index.ts";
import {usePromiseState} from "../../hooks/promiseState.ts";
import {InnerDialogProps, withDialog} from "../../state/withDialog.tsx";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog.tsx";
import {Button} from "../../components/ui/button.tsx";
import {useDialogContext} from "../../components/dialogs/index.ts";
import {createEditTeamDialog} from ".";
import T from "platformed-components/i18n/T";

const ReassignTeamDialog = withDialog(
  ({onClose, onReassign}: InnerDialogProps & {onReassign: (owner?: Owner) => Promise<unknown>}) => {
    const {open} = useDialogContext();
    const [teamOrCreate, setTeamOrCreate] = useState<TeamPickerValue | null>(null);

    const reassignTeam = useCallback(
      async (team: TeamMin) => {
        const owner = await api.owners.resolve([{type: "Team", content: team.team_id}]);
        await onReassign(owner[0]);
      },
      [onReassign],
    );

    const [reassigning, reassign] = usePromiseState(async () => {
      if (teamOrCreate?.type === "Team") {
        const owner = await api.owners.resolve([{type: "Team", content: teamOrCreate.team.team_id}]);
        await onReassign(owner[0]);
      } else if (teamOrCreate?.type === "Create") {
        // Autofocus doesn't work properly unless we add a delay between closing and re-opening a dialog
        setTimeout(
          () => open(createEditTeamDialog, {type: "Create", onCreate: reassignTeam, initialName: teamOrCreate.name}),
          200,
        );
      }
      onClose();
    }, [onReassign, onClose, teamOrCreate, open, reassignTeam]);

    return (
      <>
        <DialogHeader>
          <DialogTitle>
            <T keyName="c.Reassign_to" />
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <TeamPicker value={teamOrCreate} onSelect={setTeamOrCreate} onSubmit={reassign} autoFocus />
        </DialogBody>
        <DialogFooter>
          <HStack gap="3">
            <Button variant="outline" onClick={onClose}>
              <T keyName="c.Cancel" />
            </Button>
            <Button
              colorPalette="blue"
              onClick={reassign}
              disabled={!teamOrCreate || reassigning.inProgress}
              loading={reassigning.inProgress}
            >
              {teamOrCreate?.type === "Create" ? <T keyName="c.Create_and_reassign" /> : <T keyName="c.Reassign" />}
            </Button>
          </HStack>
        </DialogFooter>
      </>
    );
  },
);

export default ReassignTeamDialog;
